import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Single‑flight async loader with caching and abort on unmount.
 * @param {Function} loader (signal) => Promise<T>
 */
export function useAsyncResource (loader) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const inFlight = useRef(null)
  const cache = useRef(null)

  const load = useCallback(async ({ force = false } = {}) => {
    if (!force && cache.current) return cache.current
    if (inFlight.current) return inFlight.current

    const ctrl = new AbortController()
    setLoading(true)
    setError(null)

    const p = (async () => {
      try {
        const res = await loader(ctrl.signal)
        cache.current = res
        setData(res)
        return res
      } catch (e) {
        setError(e)
        throw e
      } finally {
        setLoading(false)
        inFlight.current = null
      }
    })()

    inFlight.current = p
    return p
  }, [loader])

  // abort on unmount
  useEffect(() => () => inFlight.current?.abort?.(), [])

  return useMemo(() => ({ data, error, loading, load }), [data, error, loading, load])
}
